import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { TrendingUp, Eye, ShoppingCart, Percent } from 'lucide-react';
import { caseStudies } from '../data/mock';

const CaseStudies = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const resultIcons = {
    impressions: Eye,
    engagement: TrendingUp,
    conversions: ShoppingCart,
    roi: Percent
  };

  return (
    <section id="results" className="bg-[#111111] py-24" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF3B3B] text-sm font-semibold uppercase tracking-widest mb-4 block">
            Case Studies
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Proven Campaign Results
          </h2>
          <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
            Real campaigns, real numbers, real ROI. Here's what I've delivered for premium brands.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {caseStudies.map((study, index) => (
            <Card
              key={study.id}
              className={`bg-[#1A1A1A] border-white/5 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-[#FF3B3B]/20 hover:shadow-xl hover:shadow-[#FF3B3B]/5 group ${
                isVisible ? 'animate-fadeInUp' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Header */}
              <div className="p-6 border-b border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[#FF3B3B] text-sm font-semibold uppercase tracking-wider">
                    {study.brand}
                  </span>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                </div>
                <h3 className="text-white text-xl font-bold mb-2">{study.campaign}</h3>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">
                  {study.description}
                </p>
              </div>

              {/* Results */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(study.results).map(([key, value]) => {
                    const Icon = resultIcons[key];
                    return (
                      <div key={key} className="bg-[#0B0B0B] rounded-xl p-4 group-hover:bg-[#111111] transition-colors duration-300">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon size={14} className="text-[#FF3B3B]" />
                          <span className="text-[#9CA3AF] text-xs capitalize">{key}</span>
                        </div>
                        <div className="text-white text-xl font-bold">{value}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
